/**
 * Mega Menu
 *
 * One script for both forms the navigation takes:
 *
 * - Desktop (1248px and up): dropdown panels that open on hover, or from the
 *   keyboard with the disclosure button beside each label. Escape closes and
 *   returns focus to that button; tabbing out of an item closes it. While a
 *   panel is open the header gets .has-open-menu, which frosts the page behind.
 *
 * - Mobile: a drill-down. Activating an item's disclosure button replaces the
 *   list with that item's sub-screen; focus moves to its "Back" button, and
 *   Back (or Escape) returns to the list with focus on the button that opened
 *   it.
 *
 * Markup hooks (template-parts/menu/*): [data-mega-nav] wraps the list,
 * [data-nav-item] is an item with a dropdown, [data-nav-toggle] its
 * disclosure button, [data-mega-menu] its panel and [data-nav-back] the
 * panel's Back button.
 *
 * Talks to assets/js/app.js through document events: it fires
 * 'tdwd:menu-open' when a panel opens (app.js closes the search overlay), and
 * listens for 'tdwd:search-open' and 'tdwd:nav-close' to close its panels.
 */
(function () {
    'use strict';

    var DESKTOP_QUERY = '(min-width: 1248px)';
    var CLOSE_DELAY = 150;

    function init() {
        var header = document.querySelector('[data-site-header]');
        var nav = document.querySelector('[data-mega-nav]');

        if (!header || !nav) {
            return;
        }

        var desktop = window.matchMedia(DESKTOP_QUERY);
        var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        var items = Array.prototype.slice.call(nav.querySelectorAll('[data-nav-item]'));
        var closeTimer = null;

        if (!items.length) {
            return;
        }

        function toggleOf(item) {
            return item.querySelector('[data-nav-toggle]');
        }

        function isOpen(item) {
            return item.classList.contains('is-open');
        }

        function sync() {
            var anyOpen = items.some(isOpen);

            header.classList.toggle('has-open-menu', anyOpen && desktop.matches);
            nav.classList.toggle('has-submenu-open', anyOpen && !desktop.matches);
        }

        function setOpen(item, open) {
            var toggle = toggleOf(item);

            item.classList.toggle('is-open', open);

            if (toggle) {
                toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            }
        }

        function open(item) {
            window.clearTimeout(closeTimer);

            items.forEach(function (other) {
                if (other !== item) {
                    setOpen(other, false);
                }
            });

            if (!isOpen(item)) {
                setOpen(item, true);
                document.dispatchEvent(new CustomEvent('tdwd:menu-open'));
            }

            sync();
        }

        function close(item) {
            window.clearTimeout(closeTimer);
            setOpen(item, false);
            sync();
        }

        function closeAll() {
            window.clearTimeout(closeTimer);
            items.forEach(function (item) {
                setOpen(item, false);
            });
            sync();
        }

        function closeLater(item) {
            window.clearTimeout(closeTimer);
            closeTimer = window.setTimeout(function () {
                close(item);
            }, reducedMotion.matches ? 0 : CLOSE_DELAY);
        }

        function firstLink(item) {
            return item.querySelector('[data-mega-menu] a[href]');
        }

        // Mobile: show the item's sub-screen and move focus into it.
        function drillDown(item) {
            open(item);
            nav.scrollTop = 0;

            var back = item.querySelector('[data-nav-back]');

            if (back) {
                back.focus();
            }
        }

        // Mobile: back to the list, focus on the control that opened the sub-screen.
        function drillUp(item) {
            close(item);

            var toggle = toggleOf(item);

            if (toggle) {
                toggle.focus();
            }
        }

        items.forEach(function (item) {
            var toggle = toggleOf(item);
            var link = item.querySelector('a.nav-item__link');
            var back = item.querySelector('[data-nav-back]');

            item.addEventListener('mouseenter', function () {
                if (desktop.matches) {
                    open(item);
                }
            });

            item.addEventListener('mouseleave', function () {
                if (desktop.matches) {
                    closeLater(item);
                }
            });

            if (toggle) {
                toggle.addEventListener('click', function () {
                    if (!desktop.matches) {
                        drillDown(item);
                        return;
                    }

                    if (isOpen(item)) {
                        close(item);
                    } else {
                        open(item);
                    }
                });
            }

            // Arrow Down on a label opens its panel and moves into it, as in
            // most site menus; Enter still follows the link.
            [link, toggle].forEach(function (control) {
                if (!control) {
                    return;
                }

                control.addEventListener('keydown', function (event) {
                    if (event.key !== 'ArrowDown' || !desktop.matches) {
                        return;
                    }

                    var target = firstLink(item);

                    if (target) {
                        event.preventDefault();
                        open(item);
                        target.focus();
                    }
                });
            });

            if (back) {
                back.addEventListener('click', function () {
                    drillUp(item);
                });
            }

            item.addEventListener('keydown', function (event) {
                if (event.key !== 'Escape' || !isOpen(item)) {
                    return;
                }

                // Handled here so app.js does not also close the whole mobile
                // menu: Escape steps back one level at a time.
                event.stopPropagation();

                if (desktop.matches) {
                    close(item);

                    if (toggle) {
                        toggle.focus();
                    }
                } else {
                    drillUp(item);
                }
            });

            item.addEventListener('focusout', function (event) {
                if (desktop.matches && isOpen(item) && !item.contains(event.relatedTarget)) {
                    close(item);
                }
            });
        });

        // A panel opened from the keyboard or by a tap stays open until the
        // pointer goes somewhere else on the page.
        document.addEventListener('click', function (event) {
            if (desktop.matches && !nav.contains(event.target)) {
                closeAll();
            }
        });

        document.addEventListener('tdwd:search-open', closeAll);
        document.addEventListener('tdwd:nav-close', closeAll);

        var onBreakpoint = function () {
            closeAll();
        };

        if (typeof desktop.addEventListener === 'function') {
            desktop.addEventListener('change', onBreakpoint);
        } else if (typeof desktop.addListener === 'function') {
            desktop.addListener(onBreakpoint);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
